import React, { useEffect, useState, useCallback } from 'react';
import { getPublicStatus, PublicWaitingStatus } from '../api/publicWaiting';
import styles from './PublicWaitingPage.module.css';

const POLL_INTERVAL_MS = 10_000; // 10초마다 자동 새로고침

interface Props {
  storeId: string;
}

export function PublicWaitingPage({ storeId }: Props) {
  const [status, setStatus] = useState<PublicWaitingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await getPublicStatus(storeId);
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : '대기 현황을 불러올 수 없습니다.',
      );
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchStatus();
    const timer = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchStatus]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p className={styles.loadingText}>대기 현황을 불러오는 중...</p>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className={styles.error}>
        <p className={styles.errorText}>{error}</p>
        <button className={styles.retryButton} onClick={fetchStatus}>
          다시 시도
        </button>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  // 대기 레벨: 5 이하 초록, 10 이하 노랑, 그 이상 빨강
  const level =
    status.currentWaiting <= 5
      ? 'Low'
      : status.currentWaiting <= 10
        ? 'Medium'
        : 'High';

  const bgClass =
    level === 'Low'
      ? styles.bgLow
      : level === 'Medium'
        ? styles.bgMedium
        : styles.bgHigh;

  const colorClass =
    level === 'Low'
      ? styles.colorLow
      : level === 'Medium'
        ? styles.colorMedium
        : styles.colorHigh;

  // updatedAt 포맷: HH:mm:ss
  const formatTime = (isoStr: string): string => {
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return '';
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      const s = String(d.getSeconds()).padStart(2, '0');
      return `${h}:${m}:${s}`;
    } catch {
      return '';
    }
  };

  return (
    <div className={`${styles.container} ${bgClass}`}>
      <h1 className={styles.storeName}>{status.storeName}</h1>

      <div className={styles.cards}>
        {/* 현재 대기 팀 수 */}
        <div className={styles.card}>
          <p className={styles.cardLabel}>현재 대기</p>
          <p className={`${styles.cardValue} ${colorClass}`}>
            {status.currentWaiting}
            <span className={styles.cardUnit}>팀</span>
          </p>
        </div>

        {/* 호출 중 / 마지막 호출번호 */}
        <div className={styles.subInfo}>
          <div className={styles.subCard}>
            <p className={styles.subLabel}>호출 중</p>
            <p className={styles.subValue}>
              {status.currentCalled}
              <span className={styles.subUnit}>팀</span>
            </p>
          </div>
          <div className={styles.subCard}>
            <p className={styles.subLabel}>마지막 호출번호</p>
            <p className={styles.subValue}>
              {status.lastCalledNumber > 0
                ? `${status.lastCalledNumber}번`
                : '-'}
            </p>
          </div>
        </div>

        {/* 예상 대기시간 */}
        <div className={styles.estimateCard}>
          <p className={styles.cardLabel}>예상 대기시간</p>
          <p className={`${styles.cardValue} ${colorClass}`}>
            {status.estimatedWaitMinutes > 0 ? (
              <>
                약 {status.estimatedWaitMinutes}
                <span className={styles.cardUnit}>분</span>
              </>
            ) : (
              '바로 입장'
            )}
          </p>
        </div>
      </div>

      <div className={styles.footer}>
        <p className={styles.brand}>만석 - 원격 줄서기 서비스</p>
        <p className={styles.updatedAt}>
          {formatTime(status.updatedAt)
            ? `${formatTime(status.updatedAt)} 기준`
            : ''}
        </p>
      </div>
    </div>
  );
}
